import React, { useState, useCallback, useMemo, memo, useEffect, ReactNode } from 'react';
import jsYaml from 'js-yaml';

// Type definitions
interface HelmetProps {
  children: React.ReactElement;
}

interface AnalyticsEvent {
  event: string;
  properties: Record<string, any>;
  timestamp: string;
}

interface ConversionOptions {
  jsonFormat: JsonFormat;
  indentLevel: number;
  sortKeys: boolean;
  escapeUnicode: boolean;
  includeNulls: boolean;
  yamlFlowStyle: boolean;
  yamlIndent: number;
}

interface ThemeContextType {
  isDark: boolean;
  toggleTheme: () => void;
}

interface AnalyticsHook {
  track: (event: string, properties?: Record<string, any>) => void;
}

interface SEOData {
  title: string;
  description: string;
  keywords: string;
  canonical: string;
  ogTitle: string;
  ogDescription: string;
  ogImage: string;
  twitterCard: string;
}

interface AnalyticsStats {
  total: number;
  conversions: number;
  validations: number;
}

// JSON formatting options
type JsonFormat = 'pretty' | 'compact' | 'minified';

// Mock react-helmet-async for SEO
const Helmet: React.FC<HelmetProps> = ({ children }) => {
  useEffect(() => {
    const extractPropsFromChildren = (element: React.ReactElement): any => {
      if (element.props?.children) {
        const childArray = React.Children.toArray(element.props.children);
        const helmetData: any = { meta: [], link: [] };
        
        childArray.forEach((child: any) => {
          if (React.isValidElement(child)) {
            const childElement = child as React.ReactElement<any>;
            
            if (childElement.type === 'title') {
              helmetData.title = childElement.props.children;
            } else if (childElement.type === 'meta') {
              helmetData.meta.push(childElement.props);
            } else if (childElement.type === 'link') {
              helmetData.link.push(childElement.props);
            } else if (childElement.type === 'script') {
              if (childElement.props.type === 'application/ld+json') {
                const script = document.createElement('script');
                script.type = 'application/ld+json';
                script.textContent = childElement.props.children;
                document.head.appendChild(script);
              }
            }
          }
        });
        return helmetData;
      }
      return {};
    };

    const helmetData = extractPropsFromChildren(children);
    
    if (helmetData.title) {
      document.title = helmetData.title;
    }
    
    const updateMeta = (name: string, content: string): void => {
      let meta = document.querySelector(`meta[name="${name}"]`) || 
                 document.querySelector(`meta[property="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        if (name.startsWith('og:') || name.startsWith('twitter:')) {
          meta.setAttribute('property', name);
        } else {
          meta.setAttribute('name', name);
        }
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    if (helmetData.meta && Array.isArray(helmetData.meta)) {
      helmetData.meta.forEach((meta: any) => {
        if (meta.name) updateMeta(meta.name, meta.content);
        if (meta.property) updateMeta(meta.property, meta.content);
      });
    }

    if (helmetData.link && Array.isArray(helmetData.link)) {
      helmetData.link.forEach((link: any) => {
        let linkEl = document.querySelector(`link[rel="${link.rel}"]`) as HTMLLinkElement;
        if (!linkEl) {
          linkEl = document.createElement('link') as HTMLLinkElement;
          linkEl.rel = link.rel;
          document.head.appendChild(linkEl);
        }
        Object.keys(link).forEach((attr) => {
          if (attr !== 'rel') linkEl.setAttribute(attr, link[attr]);
        });
      });
    }
  }, [children]);

  return null;
};

// Analytics hook
const useAnalytics = (): AnalyticsHook => {
  const track = useCallback((event: string, properties: Record<string, any> = {}): void => {
    console.log('Analytics Event:', event, properties);
    
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', event, properties);
    }
    
    const analytics: AnalyticsEvent[] = JSON.parse(localStorage.getItem('yamlConverter_analytics') || '[]');
    analytics.push({
      event,
      properties,
      timestamp: new Date().toISOString()
    });
    localStorage.setItem('yamlConverter_analytics', JSON.stringify(analytics.slice(-100)));
  }, []);

  return { track };
};

// Theme context
const ThemeContext = React.createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
}

const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [isDark, setIsDark] = useState<boolean>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('theme') === 'dark' || 
             (!localStorage.getItem('theme') && window.matchMedia('(prefers-color-scheme: dark)').matches);
    }
    return false;
  });

  const toggleTheme = useCallback((): void => {
    setIsDark((prev) => {
      const newTheme = !prev;
      localStorage.setItem('theme', newTheme ? 'dark' : 'light');
      return newTheme;
    });
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle('dark', isDark);
  }, [isDark]);

  return (
    <ThemeContext.Provider value={{ isDark, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

const useTheme = (): ThemeContextType => {
  const context = React.useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Enhanced YAML validation and conversion
const validateYaml = (yamlString: string): [boolean, any] => {
  try {
    const parsed = jsYaml.load(yamlString, { 
      onWarning: (warning) => console.warn('YAML Warning:', warning)
    });
    return [true, parsed];
  } catch (e) {
    return [false, e];
  }
};

const convertToJson = (data: any, options: ConversionOptions): string => {
  let result = '';
  
  // Define the replacer function for null handling
  const replacer = options.includeNulls 
    ? undefined 
    : (key: string, value: any) => value === null ? undefined : value;
  
  switch (options.jsonFormat) {
    case 'pretty':
      result = JSON.stringify(data, replacer, options.indentLevel);
      break;
    case 'compact':
      result = JSON.stringify(data, replacer);
      break;
    case 'minified':
      result = JSON.stringify(data, replacer);
      result = result.replace(/\s+/g, '');
      break;
    default:
      result = JSON.stringify(data, undefined, 2);
  }
  
  if (options.sortKeys && options.jsonFormat === 'pretty') {
    const sortedData = sortObjectKeys(data);
    result = JSON.stringify(sortedData, replacer, options.indentLevel);
  }
  
  return result;
};

const sortObjectKeys = (obj: any): any => {
  if (obj === null || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(sortObjectKeys);
  
  const sorted: any = {};
  Object.keys(obj).sort().forEach(key => {
    sorted[key] = sortObjectKeys(obj[key]);
  });
  return sorted;
};

// Analytics Dashboard Component
const AnalyticsDashboard: React.FC = memo(() => {
  const [analytics, setAnalytics] = useState<AnalyticsEvent[]>([]);
  const { isDark } = useTheme();
  
  useEffect(() => {
    const data: AnalyticsEvent[] = JSON.parse(localStorage.getItem('yamlConverter_analytics') || '[]');
    setAnalytics(data);
  }, []);
  
  const stats: AnalyticsStats = useMemo(() => {
    const total = analytics.length;
    const conversions = analytics.filter((a) => a.event === 'yaml_converted').length;
    const validations = analytics.filter((a) => a.event === 'yaml_validated').length;
    
    return { total, conversions, validations };
  }, [analytics]);
  
  return (
    <div className={`p-4 rounded-lg border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
      <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
        Usage Analytics
      </h3>
      <div className="grid grid-cols-3 gap-4">
        <div className={`text-center p-3 rounded ${isDark ? 'bg-gray-700' : 'bg-white'}`}>
          <div className={`text-2xl font-bold ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
            {stats.total}
          </div>
          <div className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            Total Events
          </div>
        </div>
        <div className={`text-center p-3 rounded ${isDark ? 'bg-gray-700' : 'bg-white'}`}>
          <div className={`text-2xl font-bold ${isDark ? 'text-green-400' : 'text-green-600'}`}>
            {stats.conversions}
          </div>
          <div className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            Conversions
          </div>
        </div>
        <div className={`text-center p-3 rounded ${isDark ? 'bg-gray-700' : 'bg-white'}`}>
          <div className={`text-2xl font-bold ${isDark ? 'text-purple-400' : 'text-purple-600'}`}>
            {stats.validations}
          </div>
          <div className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            Validations
          </div>
        </div>
      </div>
    </div>
  );
});

AnalyticsDashboard.displayName = 'AnalyticsDashboard';

// Main Component Content
const YamlToJsonContent: React.FC = () => {
  const { isDark, toggleTheme } = useTheme();
  const { track } = useAnalytics();
  
  // States
  const [yamlInput, setYamlInput] = useState<string>('');
  const [jsonOutput, setJsonOutput] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [fileName, setFileName] = useState<string>('yaml_data');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
  const [isJsonGenerated, setIsJsonGenerated] = useState<boolean>(false);
  const [copied, setCopied] = useState<boolean>(false);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [showAnalytics, setShowAnalytics] = useState<boolean>(false);
  
  // Advanced options
  const [jsonFormat, setJsonFormat] = useState<JsonFormat>('pretty');
  const [indentLevel, setIndentLevel] = useState<number>(2);
  const [sortKeys, setSortKeys] = useState<boolean>(false);
  const [escapeUnicode, setEscapeUnicode] = useState<boolean>(false);
  const [includeNulls, setIncludeNulls] = useState<boolean>(true);
  const [yamlFlowStyle, setYamlFlowStyle] = useState<boolean>(false);
  const [yamlIndent, setYamlIndent] = useState<number>(2);
  const [validateOnType, setValidateOnType] = useState<boolean>(false);
  const [autoFormat, setAutoFormat] = useState<boolean>(false);
  
  // SEO data
  const seoData: SEOData = useMemo(() => ({
    title: "Free YAML to JSON Converter Online - Convert YAML Files to JSON Format",
    description: "Convert YAML data to JSON format online for free. Advanced features include pretty printing, validation, formatting options, and real-time conversion. No registration required.",
    keywords: "YAML to JSON converter, YAML converter, JSON converter, online converter, free YAML converter, YAML parser, JSON formatter",
    canonical: "https://yoursite.com/yaml-to-json-converter",
    ogTitle: "YAML to JSON Converter - Free Online Tool",
    ogDescription: "Convert your YAML files to JSON format instantly. Supports validation, formatting, and advanced conversion options.",
    ogImage: "https://yoursite.com/og-yaml-converter.jpg",
    twitterCard: "summary_large_image"
  }), []);

  // Handle input change with real-time validation
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>): void => {
    const value = e.target.value;
    setYamlInput(value);
    setError('');
    setIsJsonGenerated(false);
    
    if (validateOnType && value.trim()) {
      const [isValid, result] = validateYaml(value);
      if (!isValid) {
        setError(`YAML Validation: ${result instanceof Error ? result.message : String(result)}`);
      }
    }
    
    track('yaml_input_changed', { length: value.length });
  }, [validateOnType, track]);

  // Enhanced example data
  const useExampleData = useCallback((): void => {
    const exampleYaml = `# Enhanced YAML Example - Cloud Infrastructure
apiVersion: v1
kind: ConfigMap
metadata:
  name: app-config
  namespace: production
  labels:
    app: web-server
    version: "2.1.0"
    environment: production
  annotations:
    deployment.kubernetes.io/revision: "3"
    description: "Main application configuration"

data:
  # Database Configuration
  database:
    host: postgres.internal.com
    port: 5432
    name: myapp_prod
    ssl_mode: require
    pool_size: 20
    timeout: 30
    
  # Redis Configuration  
  redis:
    cluster:
      - host: redis-01.cache.com
        port: 6379
      - host: redis-02.cache.com
        port: 6379
      - host: redis-03.cache.com
        port: 6379
    password: null
    database: 0
    
  # Application Settings
  app:
    name: "My Web Application"
    version: "2.1.0"
    debug: false
    log_level: "info"
    features:
      - authentication
      - file_upload
      - email_notifications
      - analytics
    limits:
      max_file_size: "10MB"
      rate_limit: 1000
      concurrent_users: 500
      
  # External Services
  external_apis:
    payment_gateway:
      url: "https://api.payment.com/v2"
      api_key: ""
      timeout: 10
      retry_attempts: 3
    email_service:
      provider: "sendgrid"
      api_key: ""
      from_email: "noreply@myapp.com"
      
  # Security Settings
  security:
    cors:
      allowed_origins:
        - "https://myapp.com"
        - "https://www.myapp.com"
      allowed_methods: ["GET", "POST", "PUT", "DELETE"]
      allowed_headers: ["Content-Type", "Authorization"]
      credentials: true
    jwt:
      secret: ""
      expiration: "24h"
      refresh_expiration: "7d"`;

    setYamlInput(exampleYaml);
    setError('');
    setIsJsonGenerated(false);
    setJsonOutput('');
    track('example_data_used');
  }, [track]);

  // Enhanced YAML formatting
  const beautifyYaml = useCallback((): void => {
    try {
      if (!yamlInput.trim()) {
        setError('Please enter YAML data to format');
        return;
      }
      
      const [isValid, parsed] = validateYaml(yamlInput);
      
      if (!isValid) {
        setError(`Invalid YAML format: ${parsed instanceof Error ? parsed.message : String(parsed)}`);
        return;
      }
      
      const beautified = jsYaml.dump(parsed, {
        indent: yamlIndent,
        lineWidth: -1,
        noRefs: true,
        noCompatMode: true,
        flowLevel: yamlFlowStyle ? 0 : -1,
        sortKeys: sortKeys
      });
      
      setYamlInput(beautified);
      setError('');
      track('yaml_formatted');
    } catch (e) {
      setError(`Error formatting YAML: ${e instanceof Error ? e.message : String(e)}`);
      track('formatting_error', { error: e instanceof Error ? e.message : String(e) });
    }
  }, [yamlInput, yamlIndent, yamlFlowStyle, sortKeys, track]);

  // Enhanced conversion
  const convertYamlToJson = useCallback(async (): Promise<void> => {
    setError('');
    setIsLoading(true);
    setIsJsonGenerated(false);
    setJsonOutput('');
    
    if (!yamlInput.trim()) {
      setError('Please enter YAML data');
      setIsLoading(false);
      return;
    }

    try {
      const startTime = Date.now();
      
      const [isValid, parsedYaml] = validateYaml(yamlInput);
      
      if (!isValid) {
        setError(`Invalid YAML format: ${parsedYaml instanceof Error ? parsedYaml.message : String(parsedYaml)}`);
        setIsLoading(false);
        track('conversion_error', { error: 'invalid_yaml' });
        return;
      }
      
      const options: ConversionOptions = {
        jsonFormat,
        indentLevel,
        sortKeys,
        escapeUnicode,
        includeNulls,
        yamlFlowStyle,
        yamlIndent
      };
      
      const formattedJson = convertToJson(parsedYaml, options);
      
      setJsonOutput(formattedJson);
      setIsJsonGenerated(true);
      setIsLoading(false);
      
      const processingTime = Date.now() - startTime;
      const yamlLines = yamlInput.split('\n').length;
      
      track('yaml_converted', {
        format: jsonFormat,
        processingTime,
        yamlLines,
        jsonLength: formattedJson.length,
        sortKeys,
        includeNulls
      });
      
    } catch (e) {
      const errorMessage = e instanceof Error ? e.message : String(e);
      setError(`Error converting YAML to JSON: ${errorMessage}`);
      setIsLoading(false);
      track('conversion_error', { error: errorMessage });
    }
  }, [yamlInput, jsonFormat, indentLevel, sortKeys, escapeUnicode, includeNulls, yamlFlowStyle, yamlIndent, track]);

  // Validate YAML only
  const validateYamlOnly = useCallback((): void => {
    if (!yamlInput.trim()) {
      setError('Please enter YAML data to validate');
      return;
    }
    
    const [isValid, result] = validateYaml(yamlInput);
    
    if (isValid) {
      setError('');
      // Show success message temporarily
      setError('✅ YAML is valid!');
      setTimeout(() => setError(''), 3000);
    } else {
      setError(`❌ Invalid YAML: ${result instanceof Error ? result.message : String(result)}`);
    }
    
    track('yaml_validated', { isValid });
  }, [yamlInput, track]);

  // Copy to clipboard
  const copyToClipboard = useCallback(async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(jsonOutput);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      track('json_copied');
    } catch (e) {
      setError('Failed to copy to clipboard');
      track('copy_error');
    }
  }, [jsonOutput, track]);

  // Download JSON file
  const downloadJson = useCallback((): void => {
    const blob = new Blob([jsonOutput], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    
    link.href = url;
    link.download = `${fileName || 'yaml_data'}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    track('json_downloaded', { fileName: `${fileName}.json` });
  }, [jsonOutput, fileName, track]);

  // Clear all
  const clearAll = useCallback((): void => {
    setYamlInput('');
    setJsonOutput('');
    setError('');
    setFileName('yaml_data');
    setIsJsonGenerated(false);
    setCopied(false);
    track('data_cleared');
  }, [track]);

  // Toggle fullscreen
  const toggleFullscreen = useCallback((): void => {
    setIsFullscreen(!isFullscreen);
    track('fullscreen_toggled', { isFullscreen: !isFullscreen });
  }, [isFullscreen, track]);

  // Handle file upload
  const handleFileUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    track('file_uploaded', { 
      fileName: file.name, 
      fileSize: file.size,
      fileType: file.type 
    });
    
    const nameParts = file.name.split('.');
    if (nameParts.length > 1) {
      nameParts.pop();
      setFileName(nameParts.join('.'));
    } else {
      setFileName(file.name);
    }
    
    const reader = new FileReader();
    reader.onload = (event): void => {
      if (event.target?.result) {
        setYamlInput(event.target.result as string);
        setError('');
        setIsJsonGenerated(false);
        setJsonOutput('');
      }
    };
    reader.onerror = (): void => {
      setError('Error reading the file');
      track('file_upload_error', { fileName: file.name });
    };
    reader.readAsText(file);
  }, [track]);

  // Auto-convert on input change
  useEffect(() => {
    if (autoFormat && yamlInput.trim() && !error) {
      const timer = setTimeout(() => {
        convertYamlToJson();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [yamlInput, autoFormat, error, convertYamlToJson]);

  return (
    <div className={`min-h-screen transition-colors duration-200 ${isDark ? 'bg-gray-900' : 'bg-gray-50'} ${isFullscreen ? 'fixed inset-0 z-50 overflow-auto' : ''}`}>
      
      {/* SEO Meta Tags */}
      <Helmet>
        <>
          <title>{seoData.title}</title>
          <meta name="description" content={seoData.description} />
          <meta name="keywords" content={seoData.keywords} />
          <link rel="canonical" href={seoData.canonical} />
          
          <meta property="og:title" content={seoData.ogTitle} />
          <meta property="og:description" content={seoData.ogDescription} />
          <meta property="og:image" content={seoData.ogImage} />
          <meta property="og:type" content="website" />
          <meta property="og:url" content={seoData.canonical} />
          
          <meta name="twitter:card" content={seoData.twitterCard} />
          <meta name="twitter:title" content={seoData.ogTitle} />
          <meta name="twitter:description" content={seoData.ogDescription} />
          <meta name="twitter:image" content={seoData.ogImage} />
          
          <meta name="robots" content="index, follow" />
          <meta name="author" content="YAML Converter Tool" />
          <meta name="viewport" content="width=device-width, initial-scale=1.0" />
          <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />
          
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "WebApplication",
              "name": "YAML to JSON Converter",
              "description": seoData.description,
              "url": seoData.canonical,
              "applicationCategory": "UtilityApplication",
              "operatingSystem": "Any",
              "offers": {
                "@type": "Offer",
                "price": "0",
                "priceCurrency": "USD"
              }
            })}
          </script>
        </>
      </Helmet>

      <div className={`${isFullscreen ? 'p-4' : 'max-w-7xl mx-auto p-6'}`}>
        
        {/* Header with Controls */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              YAML to JSON Converter
            </h1>
            <p className={`mt-2 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              Convert YAML data to JSON format with advanced validation and formatting options
            </p>
          </div>
          
          <div className="flex space-x-2">
            {/* Analytics Toggle */}
            <button
              onClick={() => setShowAnalytics(!showAnalytics)}
              className={`p-2 rounded-lg transition-colors ${isDark ? 'bg-gray-800 hover:bg-gray-700 text-white' : 'bg-white hover:bg-gray-50 text-gray-900'} border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}
              title="Toggle Analytics"
              type="button"
            >
              📊
            </button>
            
            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className={`p-2 rounded-lg transition-colors ${isDark ? 'bg-gray-800 hover:bg-gray-700 text-white' : 'bg-white hover:bg-gray-50 text-gray-900'} border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}
              title="Toggle Theme"
              type="button"
            >
              {isDark ? '☀️' : '🌙'}
            </button>
            
            {/* Fullscreen Toggle */}
            <button
              onClick={toggleFullscreen}
              className={`p-2 rounded-lg transition-colors ${isDark ? 'bg-gray-800 hover:bg-gray-700 text-white' : 'bg-white hover:bg-gray-50 text-gray-900'} border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}
              title="Toggle Fullscreen"
              type="button"
            >
              {isFullscreen ? '📱' : '🖥️'}
            </button>
          </div>
        </div>

        {/* Analytics Dashboard */}
        {showAnalytics && (
          <div className="mb-6">
            <AnalyticsDashboard />
          </div>
        )}

        {/* Description */}
        <div className={`mb-6 p-4 rounded-lg border ${isDark ? 'bg-blue-900/20 border-blue-800 text-blue-200' : 'bg-blue-50 border-blue-100 text-blue-800'}`}>
          <p>
            Convert YAML data to JSON format with advanced validation, formatting options, and real-time processing.
            Supports file uploads, syntax validation, and multiple output formats.
          </p>
        </div>
        
        <div className={`grid ${isFullscreen ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1 md:grid-cols-2'} gap-6`}>
          
          {/* Input Section */}
          <div className="flex flex-col h-full">
            <div className={`p-4 rounded-lg shadow-sm mb-4 flex-grow border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className="flex justify-between items-center mb-3">
                <label className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                  YAML Input
                </label>
                <div className="flex space-x-2">
                  <button
                    className={`text-sm transition-colors ${isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'}`}
                    onClick={beautifyYaml}
                    type="button"
                  >
                    Format YAML
                  </button>
                  <button
                    className={`text-sm transition-colors ${isDark ? 'text-green-400 hover:text-green-300' : 'text-green-600 hover:text-green-800'}`}
                    onClick={validateYamlOnly}
                    type="button"
                  >
                    Validate
                  </button>
                  <button
                    className={`text-sm transition-colors ${isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-600 hover:text-gray-800'}`}
                    onClick={() => setYamlInput('')}
                    type="button"
                  >
                    Clear
                  </button>
                </div>
              </div>
              
              {/* File Upload */}
              <div className="mb-4">
                <input
                  type="file"
                  accept=".yaml,.yml,.txt"
                  onChange={handleFileUpload}
                  className="block w-full text-sm text-gray-500
                    file:mr-4 file:py-2 file:px-4
                    file:rounded-md file:border-0
                    file:text-sm file:font-semibold
                    file:bg-blue-50 file:text-blue-700
                    hover:file:bg-blue-100"
                />
              </div>
              
              <textarea
                className={`w-full h-80 p-3 border rounded-lg font-mono text-sm transition-colors ${isDark ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:ring-blue-500 focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500'}`}
                value={yamlInput}
                onChange={handleInputChange}
                placeholder="Paste your YAML data here, e.g.,
name: John Doe
age: 30
address:
  city: New York
  zip: 10001
hobbies:
  - reading
  - hiking"
              />
            </div>
            
            {/* Configuration */}
            <div className={`p-4 rounded-lg shadow-sm border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                    Output File Name
                  </label>
                  <input
                    type="text"
                    className={`w-full p-2 border rounded-lg transition-colors ${isDark ? 'bg-gray-700 border-gray-600 text-white focus:ring-blue-500 focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500'}`}
                    value={fileName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFileName(e.target.value)}
                    placeholder="Enter file name (without extension)"
                  />
                </div>
                
                <div>
                  <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                    JSON Format
                  </label>
                  <select
                    className={`w-full p-2 border rounded-lg transition-colors ${isDark ? 'bg-gray-700 border-gray-600 text-white focus:ring-blue-500 focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500'}`}
                    value={jsonFormat}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setJsonFormat(e.target.value as JsonFormat)}
                  >
                    <option value="pretty">Pretty (indented with line breaks)</option>
                    <option value="compact">Compact (minimal whitespace)</option>
                    <option value="minified">Minified (no whitespace)</option>
                  </select>
                </div>
              </div>
              
              <div className="mb-4">
                <button
                  className={`text-sm flex items-center transition-colors ${isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'}`}
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  type="button"
                >
                  <span>{showAdvanced ? 'Hide' : 'Show'} Advanced Options</span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className={`h-4 w-4 ml-1 transition-transform ${showAdvanced ? 'transform rotate-180' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </div>
              
              {showAdvanced && (
                <div className={`space-y-4 p-3 rounded-lg mb-4 ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  
                  {/* JSON Options */}
                  <div className="space-y-3">
                    <h4 className={`font-medium text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>JSON Options</h4>
                    
                    {jsonFormat === 'pretty' && (
                      <div>
                        <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                          Indentation Level: {indentLevel} spaces
                        </label>
                        <input
                          type="range"
                          min="1"
                          max="8"
                          value={indentLevel}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setIndentLevel(parseInt(e.target.value))}
                          className="w-full"
                        />
                      </div>
                    )}
                    
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="sortKeys"
                        checked={sortKeys}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSortKeys(e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="sortKeys" className={`ml-2 block text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        Sort object keys alphabetically
                      </label>
                    </div>
                    
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="includeNulls"
                        checked={includeNulls}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setIncludeNulls(e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="includeNulls" className={`ml-2 block text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        Include null values in output
                      </label>
                    </div>
                    
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="escapeUnicode"
                        checked={escapeUnicode}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEscapeUnicode(e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="escapeUnicode" className={`ml-2 block text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        Escape unicode characters
                      </label>
                    </div>
                  </div>

                  {/* YAML Options */}
                  <div className="space-y-3">
                    <h4 className={`font-medium text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>YAML Processing Options</h4>
                    
                    <div>
                      <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        YAML Indentation: {yamlIndent} spaces
                      </label>
                      <input
                        type="range"
                        min="1"
                        max="8"
                        value={yamlIndent}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setYamlIndent(parseInt(e.target.value))}
                        className="w-full"
                      />
                    </div>
                    
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="yamlFlowStyle"
                        checked={yamlFlowStyle}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setYamlFlowStyle(e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="yamlFlowStyle" className={`ml-2 block text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        Use YAML flow style (inline format)
                      </label>
                    </div>
                    
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="validateOnType"
                        checked={validateOnType}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setValidateOnType(e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="validateOnType" className={`ml-2 block text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        Real-time validation while typing
                      </label>
                    </div>
                    
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="autoFormat"
                        checked={autoFormat}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAutoFormat(e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor="autoFormat" className={`ml-2 block text-sm ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                        Auto-convert on input change (1s delay)
                      </label>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="mt-4 flex justify-between">
                <button
                  className={`px-4 py-2 rounded-lg font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                    isDark 
                      ? 'bg-blue-600 hover:bg-blue-700 text-white focus:ring-blue-500' 
                      : 'bg-blue-600 hover:bg-blue-700 text-white focus:ring-blue-500'
                  }`}
                  onClick={convertYamlToJson}
                  disabled={isLoading}
                  type="button"
                >
                  {isLoading ? (
                    <div className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Processing...
                    </div>
                  ) : (
                    'Convert to JSON'
                  )}
                </button>
                
                <button
                  className={`px-4 py-2 rounded-lg font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                    isDark 
                      ? 'bg-gray-700 hover:bg-gray-600 text-gray-200 focus:ring-gray-500' 
                      : 'bg-gray-200 hover:bg-gray-300 text-gray-800 focus:ring-gray-500'
                  }`}
                  onClick={clearAll}
                  type="button"
                >
                  Clear All
                </button>
              </div>
            </div>
          </div>
          
          {/* Output Section */}
          <div className={`p-4 rounded-lg shadow-sm h-full flex flex-col border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className="flex justify-between items-center mb-3">
              <label className={`font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                JSON Output
              </label>
              
              {isJsonGenerated && (
                <div className="flex space-x-2">
                  <button
                    className={`text-sm flex items-center transition-colors ${isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'}`}
                    onClick={copyToClipboard}
                    type="button"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4 mr-1"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    {copied ? 'Copied!' : 'Copy JSON'}
                  </button>
                  
                  <button
                    className={`text-sm flex items-center transition-colors ${isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'}`}
                    onClick={downloadJson}
                    type="button"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4 mr-1"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    Download
                  </button>
                </div>
              )}
            </div>
            
            <div className="flex-grow flex flex-col">
              {error && (
                <div className={`border p-3 rounded-lg mb-3 ${
                  error.startsWith('✅') 
                    ? isDark ? 'bg-green-900/20 border-green-800 text-green-200' : 'bg-green-50 border-green-200 text-green-700'
                    : isDark ? 'bg-red-900/20 border-red-800 text-red-200' : 'bg-red-50 border-red-200 text-red-700'
                }`}>
                  <p className="font-medium">{error.startsWith('✅') ? 'Validation' : 'Error'}</p>
                  <p>{error}</p>
                </div>
              )}
              
              {!error && !isJsonGenerated && (
                <div className={`border p-4 rounded-lg flex-grow flex items-center justify-center ${isDark ? 'bg-gray-700 border-gray-600 text-gray-400' : 'bg-gray-50 border-gray-200 text-gray-500'}`}>
                  <div className="text-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                    <p>
                      JSON output will appear here after conversion.
                    </p>
                  </div>
                </div>
              )}
              
              {!error && isJsonGenerated && (
                <div className="flex-grow flex flex-col">
                  <textarea
                    className={`w-full flex-grow p-3 border rounded-lg font-mono text-sm transition-colors ${isDark ? 'bg-gray-700 border-gray-600 text-white focus:ring-blue-500 focus:border-blue-500' : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500'}`}
                    value={jsonOutput}
                    readOnly
                  />
                  
                  {/* Success Message */}
                  <div className={`mt-4 border p-3 rounded-lg ${isDark ? 'bg-green-900/20 border-green-800 text-green-200' : 'bg-green-50 border-green-200 text-green-700'}`}>
                    <div className="flex items-center mb-2">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <p className="font-medium">YAML Successfully Converted to JSON!</p>
                    </div>
                    <div className={`text-sm space-y-1 ${isDark ? 'text-green-300' : 'text-green-600'}`}>
                      <p><strong>Format:</strong> {jsonFormat}</p>
                      <p><strong>Output Size:</strong> {jsonOutput.length.toLocaleString()} characters</p>
                      <p><strong>Lines:</strong> {jsonOutput.split('\n').length.toLocaleString()}</p>
                      {sortKeys && <p><strong>Keys:</strong> Sorted alphabetically</p>}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Information Section */}
        <div className={`mt-8 p-6 rounded-lg shadow-sm border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <h2 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>About YAML to JSON Conversion</h2>
          
          <div className="space-y-4">
            <div>
              <h3 className={`font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>How This Converter Works</h3>
              <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                This advanced tool transforms your YAML data into valid JSON format with comprehensive validation and formatting options. 
                YAML (YAML Ain't Markup Language) is a human-friendly data serialization standard, while JSON (JavaScript Object Notation) 
                is widely used for data interchange in web applications and APIs.
              </p>
            </div>
            
            <div>
              <h3 className={`font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Advanced Features</h3>
              <ul className={`list-disc pl-5 space-y-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                <li><strong>Multiple JSON Formats</strong> - Pretty, compact, and minified output options</li>
                <li><strong>Real-time Validation</strong> - Live YAML syntax checking as you type</li>
                <li><strong>Advanced Formatting</strong> - Custom indentation, key sorting, and null handling</li>
                <li><strong>File Upload Support</strong> - Direct YAML file import with automatic processing</li>
                <li><strong>Auto-conversion</strong> - Optional real-time conversion with configurable delay</li>
                <li><strong>YAML Beautification</strong> - Format and standardize YAML structure</li>
                <li><strong>Dark/Light Theme</strong> - Comfortable editing in any environment</li>
                <li><strong>Fullscreen Mode</strong> - Distraction-free conversion workspace</li>
                <li><strong>Usage Analytics</strong> - Track conversion patterns and performance</li>
                <li><strong>SEO Optimized</strong> - Structured data for better search visibility</li>
              </ul>
            </div>
            
            <div>
              <h3 className={`font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>YAML Best Practices</h3>
              <ul className={`list-disc pl-5 space-y-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                <li>Use consistent indentation (spaces, not tabs) throughout your YAML</li>
                <li>Quote strings that contain special characters or start with numbers</li>
                <li>Use the validation feature to catch syntax errors before conversion</li>
                <li>Consider using flow style for complex nested structures</li>
                <li>Enable key sorting for consistent output across conversions</li>
                <li>Use comments in YAML to document complex configurations</li>
              </ul>
            </div>
            
            <div>
              <h3 className={`font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Performance Tips</h3>
              <ul className={`list-disc pl-5 space-y-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                <li>Use minified format for large datasets to reduce output size</li>
                <li>Disable real-time validation for very large YAML files</li>
                <li>Enable auto-conversion for rapid prototyping and testing</li>
                <li>Use compact format for network transmission and storage</li>
                <li>Pretty format is best for human-readable documentation</li>
              </ul>
            </div>
            
            <div>
              <h3 className={`font-medium mb-2 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>Supported YAML Features</h3>
              <p className={`${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                This converter supports all standard YAML 1.2 features including scalars, sequences, mappings, 
                anchors and aliases, multi-line strings, and complex data types. Advanced YAML features are 
                automatically converted to their JSON equivalents where possible.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Wrapper component with ThemeProvider
const YamlToJson: React.FC = () => {
  return (
    <ThemeProvider>
      <YamlToJsonContent />
    </ThemeProvider>
  );
};

export default YamlToJson;